<script lang="ts" setup>
import Card from 'primevue/card'
import FormSection from '@/components/forms/FormSection.vue'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import { computed, ref, watch } from 'vue'
import InputLabel from '@/components/inputs/InputLabel.vue'
import { FabricStatusEnum } from '@/types/product-types'
import { unitOptions, fabricWidthOptions } from '@/constants/dimension'

const props = defineProps<{
  values: any
  type: 'add' | 'edit' | 'view'
  onSubmit: () => void
  setErrors: (errors: any) => void
}>()

const fabricStatus = [
  { name: 'Finished', value: FabricStatusEnum.Finished },
  { name: 'Unfinished', value: FabricStatusEnum.Unfinished }
]

const isViewPage = computed<boolean>(() => props.type === 'view')
const isEditOrAddPage = computed<boolean>(() => props.type === 'edit' || props.type === 'add')

const selectedFabricStatus = ref<FabricStatusEnum>(
  props.values?.fabricStatus ?? FabricStatusEnum.Finished
)

watch(
  () => props.values?.fabricStatus,
  (newStatus) => {
    if (newStatus !== undefined) {
      selectedFabricStatus.value = newStatus
    }
  }
)
</script>

<template>
  <form @submit.prevent="onSubmit" class="border-t border-neutral-200/50 !rounded-t-3xl">
    <card>
      <template #content>
        <div class="">
          <FormSection
            title="Product Specification Details"
            :description="`${type === 'view' ? '' : 'Enter the product specification details'}`"
          >
            <div>
              <template v-if="isViewPage">
                <InputLabel>Fabric Status</InputLabel>
                <p class="py-2 font-medium text-[15px] bg-gray-50 rounded-lg px-2">
                  {{
                    selectedFabricStatus === FabricStatusEnum.Finished ? 'Finished' : 'Unfinished'
                  }}
                </p>
              </template>
              <template v-else>
                <FormDropdown
                  label="Fabric Status"
                  inputName="fabricStatus"
                  id="fabricStatus"
                  :options="fabricStatus"
                  optionLabel="name"
                  optionValue="value"
                />
              </template>
            </div>

            <template v-if="selectedFabricStatus === 0">
              <FormInputText
                :view-only="isViewPage"
                label="Reference"
                inputName="finishedReference"
                id="finishedReference"
              />

              <FormInputText
                :view-only="isViewPage"
                label="Alternative Reference"
                inputName="alternateReference"
                id="alternateReference"
              />

              <FormInputText
                :view-only="isViewPage"
                label="Displayed Reference"
                inputName="displayedReference"
                id="displayedReference"
              />

              <FormInputText
                :view-only="isViewPage"
                label="Colour"
                inputName="colour"
                id="colour"
              />

              <FormInputText
                :view-only="isViewPage"
                label="Finish"
                inputName="finish"
                id="finish"
              />
              <div class="flex w-full items-center gap-2">
                <div class="flex flex-col w-full">
                  <FormInputText
                    :view-only="isViewPage"
                    label="Fabric Width"
                    inputName="fabricWidthValue"
                    id="fabricWidthValue"
                    type="number"
                  />
                </div>
                <div class="w-1/4">
                  <FormDropdown
                    label="Unit"
                    inputName="fabricWidthUnit"
                    id="fabricWidthUnit"
                    :view-only="isViewPage"
                    :options="fabricWidthOptions"
                    optionLabel="label"
                    optionValue="value"
                    type="number"
                  />
                </div>
              </div>
              <div class="flex w-full items-center gap-2">
                <div class="flex flex-col w-full">
                  <FormInputText
                    :view-only="isViewPage"
                    label="Fabric Yield (Metres/Yard per KG)"
                    inputName="fabricYieldValue"
                    id="fabricYieldValue"
                    type="number"
                  />
                </div>
                <div class="w-1/4">
                  <FormDropdown
                    label="Unit"
                    inputName="fabricYieldUnit"
                    id="fabricYieldUnit"
                    :view-only="isViewPage"
                    :options="unitOptions"
                    optionLabel="label"
                    optionValue="value"
                    type="number"
                  />
                </div>
              </div>
              <div class="flex w-full items-center gap-2">
                <div class="flex flex-col w-full">
                  <FormInputText
                    :view-only="isViewPage"
                    label="Average Roll Length"
                    inputName="averageRollLengthValue"
                    id="averageRollLengthValue"
                    type="number"
                  />
                </div>
                <div class="w-1/4">
                  <FormDropdown
                    label="Unit"
                    inputName="averageRollLengthUnit"
                    id="averageRollLengthUnit"
                    :view-only="isViewPage"
                    :options="unitOptions"
                    optionLabel="label"
                    optionValue="value"
                    type="number"
                  />
                </div>
              </div>
            </template>

            <template v-else-if="selectedFabricStatus === 1">
              <FormInputText
                :view-only="isViewPage"
                :model-value="values?.unfinishedReference"
                label="Reference"
                inputName="unfinishedReference"
                id="unfinishedReference"
              />

              <FormInputText :model-value="values?.guage" :view-only="isViewPage" label="Guage" inputName="guage" id="guage" />
            </template>

            <FormInputText
              :view-only="isViewPage"
              label="Construction"
              inputName="construction"
              id="construction"
            />

            <FormInputText
              :view-only="isViewPage"
              label="Fabric Weight (gsm)"
              inputName="fabricWeight"
              id="fabricWeight"
              type="number"
            />

            <FormInputText
              :view-only="isViewPage"
              label="Fibre Content"
              inputName="fibreContent"
              id="fibreContent"
            />
            <FormInputText
              type="number"
              :view-only="isViewPage"
              label="Standard Batch Size"
              inputName="standardBatchSize"
              id="standardBatchSize"
            />
          </FormSection>
        </div>
      </template>
    </card>
  </form>
</template>
